import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { DynamicFormRenderer } from '../components/forms/DynamicFormRenderer';

export default function FillForm() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState(null);
  const [template, setTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadAssignment();
  }, [assignmentId]);

  const loadAssignment = async () => {
    try {
      // Get assignment
      const assignmentsRes = await api.get('/api/forms/assignments');
      const assign = assignmentsRes.data.find(a => a.id === assignmentId);
      
      if (!assign) {
        toast.error('Form assignment not found');
        navigate('/dashboard');
        return;
      }
      
      setAssignment(assign);
      
      // Get template
      const templatesRes = await api.get('/api/forms/templates');
      const tmpl = templatesRes.data.find(t => t.id === assign.template_id);
      
      if (tmpl) {
        setTemplate(tmpl);
      }
    } catch (error) {
      toast.error('Failed to load form');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldId, value) => {
    setFormData({ ...formData, [fieldId]: value });
    
    // Clear error for this field
    if (errors[fieldId]) {
      const newErrors = { ...errors };
      delete newErrors[fieldId];
      setErrors(newErrors);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    template.fields_schema.forEach(field => {
      if (field.required && !formData[field.id]) {
        newErrors[field.id] = `${field.label} is required`;
      }
      
      if (field.validation && formData[field.id]) {
        const regex = new RegExp(field.validation);
        if (!regex.test(formData[field.id])) {
          newErrors[field.id] = `Invalid format for ${field.label}`;
        }
      }
    });
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please fix validation errors');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/api/forms/submissions', {
        assignment_id: assignmentId,
        form_data: formData
      });
      
      toast.success('Form submitted successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !template) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (assignment.status === 'completed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted px-4">
        <Card className="w-full max-w-2xl shadow-lg">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
              Form Already Completed
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted-foreground">This form has already been submitted.</p>
            <Button onClick={() => navigate('/dashboard')} className="mt-4">Back to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Card className="shadow-lg">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
                  {template.name}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
              </div>
              <Badge variant="outline">{template.fields_schema.length} fields</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <DynamicFormRenderer
                fields={template.fields_schema}
                formData={formData}
                onChange={handleFieldChange}
                errors={errors}
              />

              <div className="flex justify-end gap-2 pt-6 border-t">
                <Button type="button" variant="outline" onClick={() => navigate('/dashboard')}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="bg-primary hover:bg-primary/90">
                  {submitting ? 'Submitting...' : 'Submit Form'}
                  <Send className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
